import React, { useState } from 'react';
import { 
    Form, 
    Input, 
    Button, 
    message, 
    Spin, 
    Card, 
    Row, 
    Col, 
    Typography, 
    Table, 
    Tag, 
    Modal, 
    DatePicker, 
    Select,
    Space,
    Divider,
    Switch
} from 'antd';
import { UserOutlined, SearchOutlined, SaveOutlined, HistoryOutlined, EditOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import { CustomerProfileDTO } from '@/shared/api/model';
import dayjs from 'dayjs';
import {
    showSuccessNotification,
    showErrorNotification
} from "@/components/common/utils/notify";
import { FspType, EmploymentType } from '@/shared/api/model';
import { useSearchProfiles, useGetProfileById, useAdminUpdateProfile, useGetAuditLogs, useSearchCustomersWithProfileStatus, useCreateProfile } from '../hooks/useAdminProfileManagement';
import { PaymentInformationSection } from '@/views/profile/PaymentInformationSection';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface ProfileAuditLog {
    id: number;
    institutionId: string;
    customerId: string;
    adminUserId: string;
    adminUsername: string;
    actionType: string;
    fieldName: string | null;
    oldValue: string | null;
    newValue: string | null;
    changeReason: string | null;
    ipAddress: string | null;
    createdAt: string;
}

interface CustomerWithProfileStatus {
    branch: string;
    custNo: string;
    name: string;
    phone: string | null;
    mobile: string | null;
    address: string | null;
    dobirth: string | null;
    custSex: string | null;
    idCard: string | null;
    groupCode: string | null;
    groupName: string | null;
    hasProfile: boolean;
    hasNubUser: boolean;
}

export const AdminProfileManagementView: React.FC = () => {
    const [form] = Form.useForm();
    const { dark } = useTheme();
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<CustomerProfileDTO[]>([]);
    const [customerSearchResults, setCustomerSearchResults] = useState<CustomerWithProfileStatus[]>([]);
    const [searchMode, setSearchMode] = useState<'profiles' | 'customers'>('profiles');
    const [selectedProfile, setSelectedProfile] = useState<CustomerProfileDTO | null>(null);
    const [isProfileModalVisible, setIsProfileModalVisible] = useState(false);
    const [isAuditModalVisible, setIsAuditModalVisible] = useState(false);
    const [auditLogs, setAuditLogs] = useState<ProfileAuditLog[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const searchMutation = useSearchProfiles();
    const searchCustomersMutation = useSearchCustomersWithProfileStatus();
    const createProfileMutation = useCreateProfile();
    const updateMutation = useAdminUpdateProfile();

    const handleSearch = async () => {
        if (!searchQuery.trim()) {
            message.warning('Please enter a search query');
            return;
        }

        setIsLoading(true);
        try {
            if (searchMode === 'profiles') {
                const response = await searchMutation.mutateAsync(searchQuery);
                setSearchResults(response.data || []);
                setCustomerSearchResults([]);
                if (response.data && response.data.length === 0) {
                    message.info('No profiles found matching your search');
                } else if (response.data && response.data.length > 0) {
                    message.success(`Found ${response.data.length} profile(s)`);
                }
            } else {
                const response = await searchCustomersMutation.mutateAsync(searchQuery);
                setCustomerSearchResults(response.data || []);
                setSearchResults([]);
                if (response.data && response.data.length === 0) {
                    message.info('No customers found matching your search');
                } else if (response.data && response.data.length > 0) {
                    const missingProfiles = response.data.filter((c: CustomerWithProfileStatus) => !c.hasProfile).length;
                    message.success(`Found ${response.data.length} customer(s), ${missingProfiles} missing profile(s)`);
                }
            }
        } catch (error) {
            showErrorNotification('Failed to search');
            setSearchResults([]);
            setCustomerSearchResults([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleViewProfile = async (profile: CustomerProfileDTO) => {
        setIsLoading(true);
        try {
            setSelectedProfile(profile);
            form.setFieldsValue({
                ...profile,
                birthDate: profile.birthDate ? dayjs(profile.birthDate) : null,
                startDate: profile.startDate ? dayjs(profile.startDate) : null,
                endDate: profile.endDate ? dayjs(profile.endDate) : null,
                confirmationDate: profile.confirmationDate ? dayjs(profile.confirmationDate) : null,
            });
            setIsProfileModalVisible(true);
        } catch (error) {
            showErrorNotification('Failed to load profile');
        } finally {
            setIsLoading(false);
        }
    };

    const handleUpdateProfile = async (values: any) => {
        if (!selectedProfile) return;

        setIsSaving(true);
        try {
            const updateData: Partial<CustomerProfileDTO> = {
                ...values,
                birthDate: values.birthDate ? values.birthDate.format('YYYY-MM-DD') : undefined,
                startDate: values.startDate ? values.startDate.format('YYYY-MM-DD') : undefined,
                endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : undefined,
                confirmationDate: values.confirmationDate ? values.confirmationDate.format('YYYY-MM-DD') : undefined,
            };

            await updateMutation.mutateAsync({
                customerId: selectedProfile.customerId!,
                data: updateData as CustomerProfileDTO
            });
            
            showSuccessNotification('Profile updated successfully');
            setIsProfileModalVisible(false);
            setSelectedProfile(null);
            // Refresh search results
            if (searchQuery) {
                handleSearch();
            }
        } catch (error) {
            showErrorNotification('Failed to update profile');
        } finally {
            setIsSaving(false);
        }
    };

    const handleViewAuditLogs = async (profile: CustomerProfileDTO) => {
        setIsLoading(true);
        try {
            // For now, this is a placeholder since we haven't implemented the audit logs endpoint yet
            // In the future, use: const response = await getAuditLogs(profile.customerId!);
            message.info('Audit logs functionality - endpoint to be implemented');
            setAuditLogs([]);
            setIsAuditModalVisible(true);
        } catch (error) {
            showErrorNotification('Failed to load audit logs');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateProfile = async (customer: CustomerWithProfileStatus) => {
        setIsLoading(true);
        try {
            const customerId = `${customer.branch}.${customer.custNo}`;
            
            // Create a new profile with customer information
            const newProfile: Partial<CustomerProfileDTO> = {
                customerId: customerId,
                fullName: customer.name,
                phoneNumber: customer.mobile || customer.phone,
                branch: customer.branch,
                birthDate: customer.dobirth,
                // Set default values for required fields
                entityStatus: 'ACTIVE' as any,
                acceptedTerms: false,
            };

            await createProfileMutation.mutateAsync(newProfile as CustomerProfileDTO);
            
            showSuccessNotification('Profile created successfully');
            
            // Refresh search results
            if (searchQuery) {
                handleSearch();
            }
        } catch (error) {
            showErrorNotification('Failed to create profile');
        } finally {
            setIsLoading(false);
        }
    };

    const resultColumns = [
        {
            title: 'Customer ID',
            dataIndex: 'customerId',
            key: 'customerId',
            width: 150,
        },
        {
            title: 'Full Name',
            dataIndex: 'fullName',
            key: 'fullName',
            width: 200,
        },
        {
            title: 'Phone Number',
            dataIndex: 'phoneNumber',
            key: 'phoneNumber',
            width: 150,
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            width: 200,
            ellipsis: true,
        },
        {
            title: 'Employer',
            dataIndex: 'employerName',
            key: 'employerName',
            width: 200,
            ellipsis: true,
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            render: (_: any, record: CustomerProfileDTO) => (
                <Space>
                    <Button
                        type="primary"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleViewProfile(record)}
                    >
                        Edit
                    </Button>
                    <Button
                        size="small"
                        icon={<HistoryOutlined />}
                        onClick={() => handleViewAuditLogs(record)}
                    >
                        History
                    </Button>
                </Space>
            ),
        },
    ];

    const customerSearchColumns = [
        {
            title: 'Customer ID',
            key: 'customerId',
            width: 120,
            render: (_: any, record: CustomerWithProfileStatus) => `${record.branch}.${record.custNo}`,
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 200,
        },
        {
            title: 'Phone',
            dataIndex: 'mobile',
            key: 'mobile',
            width: 150,
        },
        {
            title: 'Group',
            dataIndex: 'groupName',
            key: 'groupName',
            width: 150,
        },
        {
            title: 'Profile Status',
            key: 'profileStatus',
            width: 120,
            render: (_: any, record: CustomerWithProfileStatus) => (
                record.hasProfile ? (
                    <Tag color="green">Has Profile</Tag>
                ) : (
                    <Tag color="red">Missing Profile</Tag>
                )
            ),
        },
        {
            title: 'NUB User',
            key: 'nubUser',
            width: 100,
            render: (_: any, record: CustomerWithProfileStatus) => (
                record.hasNubUser ? (
                    <Tag color="blue">Yes</Tag>
                ) : (
                    <Tag color="default">No</Tag>
                )
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            render: (_: any, record: CustomerWithProfileStatus) => (
                <Space>
                    {!record.hasProfile && (
                        <Button
                            type="primary"
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={() => handleCreateProfile(record)}
                            loading={isLoading}
                        >
                            Create Profile
                        </Button>
                    )}
                </Space>
            ),
        },
    ];

    const auditColumns = [
        {
            title: 'Date/Time',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm:ss'),
            width: 180,
        },
        {
            title: 'Admin User',
            dataIndex: 'adminUsername',
            key: 'adminUsername',
            width: 150,
        },
        {
            title: 'Action',
            dataIndex: 'actionType',
            key: 'actionType',
            render: (action: string) => (
                <Tag color={action === 'UPDATE' ? 'blue' : 'green'}>{action}</Tag>
            ),
            width: 100,
        },
        {
            title: 'Field',
            dataIndex: 'fieldName',
            key: 'fieldName',
            width: 150,
        },
        {
            title: 'Old Value',
            dataIndex: 'oldValue',
            key: 'oldValue',
            ellipsis: true,
            width: 200,
        },
        {
            title: 'New Value',
            dataIndex: 'newValue',
            key: 'newValue',
            ellipsis: true,
            width: 200,
        },
        {
            title: 'Reason',
            dataIndex: 'changeReason',
            key: 'changeReason',
            ellipsis: true,
            width: 200,
        },
        {
            title: 'IP Address',
            dataIndex: 'ipAddress',
            key: 'ipAddress',
            width: 130,
        },
    ];

    return (
        <div style={{ padding: '24px' }}>
            <Title level={2}>Admin Profile Management</Title>
            <Text type="secondary">Search and manage customer profiles</Text>
            
            <Divider />
            
            {/* Search Section */}
            <Card style={{ marginBottom: '24px' }}>
                <Row gutter={16} align="middle" style={{ marginBottom: '16px' }}>
                    <Col>
                        <Space>
                            <Text>Search Mode:</Text>
                            <Switch
                                checked={searchMode === 'customers'}
                                onChange={(checked) => {
                                    setSearchMode(checked ? 'customers' : 'profiles');
                                    setSearchResults([]);
                                    setCustomerSearchResults([]);
                                }}
                                checkedChildren="Customers"
                                unCheckedChildren="Profiles"
                            />
                        </Space>
                    </Col>
                </Row>
                <Row gutter={16} align="middle">
                    <Col flex="auto">
                        <Input
                            size="large"
                            placeholder={searchMode === 'customers' ? "Search by customer name..." : "Search by phone number, customer ID, or name..."}
                            prefix={<SearchOutlined />}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onPressEnter={handleSearch}
                        />
                    </Col>
                    <Col>
                        <Button 
                            type="primary" 
                            size="large" 
                            icon={<SearchOutlined />}
                            onClick={handleSearch}
                            loading={isLoading}
                        >
                            Search
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* Results Table */}
            <Card>
                {searchMode === 'profiles' ? (
                    searchResults.length > 0 ? (
                        <Table
                            columns={resultColumns}
                            dataSource={searchResults}
                            rowKey="customerId"
                            pagination={{ pageSize: 10 }}
                            scroll={{ x: 1200 }}
                        />
                    ) : (
                        <Text type="secondary">
                            {searchQuery ? 'No search results found' : 'Search results will appear here'}
                        </Text>
                    )
                ) : (
                    customerSearchResults.length > 0 ? (
                        <Table
                            columns={customerSearchColumns}
                            dataSource={customerSearchResults}
                            rowKey={(record) => `${record.branch}.${record.custNo}`}
                            pagination={{ pageSize: 10 }}
                            scroll={{ x: 1000 }}
                        />
                    ) : (
                        <Text type="secondary">
                            {searchQuery ? 'No search results found' : 'Search results will appear here'}
                        </Text>
                    )
                )}
            </Card>

            {/* Profile Edit Modal */}
            <Modal
                title="Edit Customer Profile"
                open={isProfileModalVisible}
                onCancel={() => setIsProfileModalVisible(false)}
                footer={null}
                width={800}
            >
                {selectedProfile && (
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleUpdateProfile}
                        initialValues={{
                            ...selectedProfile,
                            birthDate: selectedProfile.birthDate ? dayjs(selectedProfile.birthDate) : null,
                            startDate: selectedProfile.startDate ? dayjs(selectedProfile.startDate) : null,
                            endDate: selectedProfile.endDate ? dayjs(selectedProfile.endDate) : null,
                            confirmationDate: selectedProfile.confirmationDate ? dayjs(selectedProfile.confirmationDate) : null,
                        }}
                    >
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Full Name"
                                    name="fullName"
                                    rules={[{ required: true, message: 'Please enter full name' }]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="Phone Number"
                                    name="phoneNumber"
                                    rules={[{ required: true, message: 'Please enter phone number' }]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Email"
                                    name="email"
                                >
                                    <Input type="email" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="Nickname"
                                    name="nickname"
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Employment Type"
                                    name="employmentType"
                                >
                                    <Select>
                                        <Select.Option value={EmploymentType.CONTRACTUAL}>Contractual</Select.Option>
                                        <Select.Option value={EmploymentType.PERMANENT}>Permanent</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="Employer Name"
                                    name="employerName"
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Employee ID"
                                    name="employeeId"
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="Vote Number"
                                    name="voteNumber"
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Employer Contacts"
                                    name="employerContacts"
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="Employer Address"
                                    name="employerAddress"
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider>Personal Information</Divider>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Birth Date"
                                    name="birthDate"
                                >
                                    <DatePicker style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="Marital Status"
                                    name="maritalStatus"
                                >
                                    <Select>
                                        <Select.Option value="SINGLE">Single</Select.Option>
                                        <Select.Option value="MARRIED">Married</Select.Option>
                                        <Select.Option value="DIVORCED">Divorced</Select.Option>
                                        <Select.Option value="WIDOWED">Widowed</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="NIDA Number"
                                    name="nida"
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="Confirmation Date"
                                    name="confirmationDate"
                                >
                                    <DatePicker style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider>Next of Kin</Divider>

                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    label="Next of Kin Name"
                                    name="nextOfKinName"
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    label="Next of Kin Phone"
                                    name="nextOfKinPhoneNumber"
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    label="Relationship"
                                    name="nextOfKinRelationship"
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider>Payment Information</Divider>

                        <PaymentInformationSection
                            form={form}
                            initialValues={selectedProfile}
                        />

                        <Form.Item
                            label="Change Reason"
                            name="changeReason"
                            rules={[{ required: true, message: 'Please provide a reason for this change' }]}
                        >
                            <TextArea rows={3} placeholder="Explain why you are making these changes..." />
                        </Form.Item>

                        <Form.Item>
                            <Space>
                                <Button 
                                    type="primary" 
                                    htmlType="submit" 
                                    icon={<SaveOutlined />}
                                    loading={isSaving}
                                >
                                    Save Changes
                                </Button>
                                <Button onClick={() => setIsProfileModalVisible(false)}>
                                    Cancel
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                )}
            </Modal>

            {/* Audit Logs Modal */}
            <Modal
                title="Profile Change History"
                open={isAuditModalVisible}
                onCancel={() => setIsAuditModalVisible(false)}
                footer={null}
                width={1200}
            >
                <Table
                    columns={auditColumns}
                    dataSource={auditLogs}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 1200 }}
                />
            </Modal>
        </div>
    );
};
